/* ============================================================
   Natural Roots — Product Spotlight (bakhoor)
   1. NRSmoke: a small WebGL "stable fluids" smoke simulation.
      Smoke rises from a hidden source behind the product, curls
      with vorticity, drifts with a light breeze and fades out.
      It only runs while the section is on screen, stops when the
      tab is hidden, and draws one still frame for reduced motion.
   2. Section behaviour: reveal on scroll, quantity stepper and
      add-to-cart (same flow as nr-product-tabs: cart/add.js then
      open the cart drawer).
   ============================================================ */
(function () {
  'use strict';
  if (window.NRSpotlight) {
    window.NRSpotlight.boot(document);
    return;
  }

  /* ---------------------------------------------------------------- */
  /* Shaders                                                          */
  /* ---------------------------------------------------------------- */
  var FRAG_HEAD =
    '#ifdef GL_FRAGMENT_PRECISION_HIGH\n' +
    'precision highp float;\nprecision highp sampler2D;\n' +
    '#else\n' +
    'precision mediump float;\nprecision mediump sampler2D;\n' +
    '#endif\n' +
    'varying vec2 vUv;\nvarying vec2 vL;\nvarying vec2 vR;\nvarying vec2 vT;\nvarying vec2 vB;\n';

  var VERT =
    'precision highp float;\n' +
    'attribute vec2 aPosition;\n' +
    'varying vec2 vUv;\nvarying vec2 vL;\nvarying vec2 vR;\nvarying vec2 vT;\nvarying vec2 vB;\n' +
    'uniform vec2 texelSize;\n' +
    'void main () {\n' +
    '  vUv = aPosition * 0.5 + 0.5;\n' +
    '  vL = vUv - vec2(texelSize.x, 0.0);\n' +
    '  vR = vUv + vec2(texelSize.x, 0.0);\n' +
    '  vT = vUv + vec2(0.0, texelSize.y);\n' +
    '  vB = vUv - vec2(0.0, texelSize.y);\n' +
    '  gl_Position = vec4(aPosition, 0.0, 1.0);\n' +
    '}\n';

  var FS = {
    scale:
      'uniform sampler2D uTexture;\nuniform float value;\n' +
      'void main () { gl_FragColor = value * texture2D(uTexture, vUv); }\n',

    source:
      'uniform sampler2D uTarget;\nuniform float aspectRatio;\nuniform vec4 value;\n' +
      'uniform vec2 point;\nuniform float radius;\nuniform float blend;\n' +
      'void main () {\n' +
      '  vec2 p = vUv - point;\n' +
      '  p.x *= aspectRatio;\n' +
      '  float m = exp(-dot(p, p) / radius);\n' +
      '  vec4 base = texture2D(uTarget, vUv);\n' +
      '  gl_FragColor = mix(base + value * m, mix(base, value, m), blend);\n' +
      '}\n',

    advect:
      'uniform sampler2D uVelocity;\nuniform sampler2D uSource;\n' +
      'uniform vec2 uVelTexel;\nuniform vec2 uSrcTexel;\nuniform float dt;\nuniform float dissipation;\n' +
      'vec4 bilerp (sampler2D sam, vec2 uv, vec2 ts) {\n' +
      '  vec2 st = uv / ts - 0.5;\n' +
      '  vec2 i = floor(st);\n' +
      '  vec2 f = fract(st);\n' +
      '  vec4 a = texture2D(sam, (i + vec2(0.5, 0.5)) * ts);\n' +
      '  vec4 b = texture2D(sam, (i + vec2(1.5, 0.5)) * ts);\n' +
      '  vec4 c = texture2D(sam, (i + vec2(0.5, 1.5)) * ts);\n' +
      '  vec4 d = texture2D(sam, (i + vec2(1.5, 1.5)) * ts);\n' +
      '  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);\n' +
      '}\n' +
      'void main () {\n' +
      '#ifdef MANUAL_FILTERING\n' +
      '  vec2 coord = vUv - dt * bilerp(uVelocity, vUv, uVelTexel).xy * uVelTexel;\n' +
      '  vec4 result = bilerp(uSource, coord, uSrcTexel);\n' +
      '#else\n' +
      '  vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * uVelTexel;\n' +
      '  vec4 result = texture2D(uSource, coord);\n' +
      '#endif\n' +
      '  gl_FragColor = result / (1.0 + dissipation * dt);\n' +
      '}\n',

    curl:
      'uniform sampler2D uVelocity;\n' +
      'void main () {\n' +
      '  float L = texture2D(uVelocity, vL).y;\n' +
      '  float R = texture2D(uVelocity, vR).y;\n' +
      '  float T = texture2D(uVelocity, vT).x;\n' +
      '  float B = texture2D(uVelocity, vB).x;\n' +
      '  gl_FragColor = vec4(0.5 * (R - L - T + B), 0.0, 0.0, 1.0);\n' +
      '}\n',

    /* vorticity confinement + buoyancy + breeze + curl-noise turbulence */
    forces:
      'uniform sampler2D uVelocity;\nuniform sampler2D uCurl;\nuniform sampler2D uDensity;\n' +
      'uniform float curl;\nuniform float buoyancy;\nuniform float dt;\nuniform float time;\n' +
      'uniform float wind;\nuniform float turbulence;\nuniform float aspect;\n' +
      'uniform float ambientDamp;\nuniform float maxSpeed;\nuniform float confineFloor;\n' +
      'float hash (vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }\n' +
      'float vnoise (vec2 p) {\n' +
      '  vec2 i = floor(p);\n  vec2 f = fract(p);\n  vec2 u = f * f * (3.0 - 2.0 * f);\n' +
      '  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);\n' +
      '}\n' +
      'void main () {\n' +
      '  float L = texture2D(uCurl, vL).x;\n' +
      '  float R = texture2D(uCurl, vR).x;\n' +
      '  float T = texture2D(uCurl, vT).x;\n' +
      '  float B = texture2D(uCurl, vB).x;\n' +
      '  float C = texture2D(uCurl, vUv).x;\n' +
      '  vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));\n' +
      '  force /= length(force) + 0.0001;\n' +
      '  force *= curl * C;\n' +
      '  force.y *= -1.0;\n' +
      '  vec2 vel = texture2D(uVelocity, vUv).xy;\n' +
      '  float d = texture2D(uDensity, vUv).x;\n' +
      '  float smoke = smoothstep(0.02, 0.25, d);\n' +
      '  vel += force * dt * mix(confineFloor, 1.0, smoke);\n' +
      '  vel.y += buoyancy * d * dt;\n' +
      '  float h = smoothstep(0.12, 0.9, vUv.y);\n' +
      '  vec2 q = vec2(vUv.x * aspect, vUv.y) * 3.2 + vec2(time * 0.05, -time * 0.32);\n' +
      '  float e = 0.06;\n' +
      '  float n1 = vnoise(q + vec2(0.0, e));\n  float n2 = vnoise(q - vec2(0.0, e));\n' +
      '  float n3 = vnoise(q + vec2(e, 0.0));\n  float n4 = vnoise(q - vec2(e, 0.0));\n' +
      '  vec2 cn = vec2(n1 - n2, n4 - n3) / (2.0 * e);\n' +
      '  vel += (vec2(wind, 0.0) + cn * turbulence) * h * dt;\n' +
      '  vel /= 1.0 + dt * ambientDamp * (1.0 - smoke);\n' +
      '  float sp = length(vel);\n' +
      '  if (sp > maxSpeed) { vel *= maxSpeed / sp; }\n' +
      '  gl_FragColor = vec4(vel, 0.0, 1.0);\n' +
      '}\n',

    /* open top and sides, solid floor */
    divergence:
      'uniform sampler2D uVelocity;\n' +
      'void main () {\n' +
      '  float L = texture2D(uVelocity, vL).x;\n' +
      '  float R = texture2D(uVelocity, vR).x;\n' +
      '  float T = texture2D(uVelocity, vT).y;\n' +
      '  float B = texture2D(uVelocity, vB).y;\n' +
      '  vec2 C = texture2D(uVelocity, vUv).xy;\n' +
      '  if (vB.y < 0.0) { B = -C.y; }\n' +
      '  gl_FragColor = vec4(0.5 * (R - L + T - B), 0.0, 0.0, 1.0);\n' +
      '}\n',

    pressure:
      'uniform sampler2D uPressure;\nuniform sampler2D uDivergence;\n' +
      'void main () {\n' +
      '  float L = texture2D(uPressure, vL).x;\n' +
      '  float R = texture2D(uPressure, vR).x;\n' +
      '  float T = texture2D(uPressure, vT).x;\n' +
      '  float B = texture2D(uPressure, vB).x;\n' +
      '  if (vL.x < 0.0) { L = 0.0; }\n' +
      '  if (vR.x > 1.0) { R = 0.0; }\n' +
      '  if (vT.y > 1.0) { T = 0.0; }\n' +
      '  float div = texture2D(uDivergence, vUv).x;\n' +
      '  gl_FragColor = vec4((L + R + B + T - div) * 0.25, 0.0, 0.0, 1.0);\n' +
      '}\n',

    gradient:
      'uniform sampler2D uPressure;\nuniform sampler2D uVelocity;\nuniform float gradScale;\n' +
      'void main () {\n' +
      '  float L = texture2D(uPressure, vL).x;\n' +
      '  float R = texture2D(uPressure, vR).x;\n' +
      '  float T = texture2D(uPressure, vT).x;\n' +
      '  float B = texture2D(uPressure, vB).x;\n' +
      '  if (vL.x < 0.0) { L = 0.0; }\n' +
      '  if (vR.x > 1.0) { R = 0.0; }\n' +
      '  if (vT.y > 1.0) { T = 0.0; }\n' +
      '  vec2 vel = texture2D(uVelocity, vUv).xy;\n' +
      '  vel -= gradScale * vec2(R - L, T - B);\n' +
      '  gl_FragColor = vec4(vel, 0.0, 1.0);\n' +
      '}\n',

    /* density -> premultiplied colour with soft self-shading */
    display:
      'uniform sampler2D uTexture;\n' +
      'uniform vec3 colorLow;\nuniform vec3 colorHigh;\nuniform vec3 glowColor;\nuniform vec2 glowPos;\nuniform float glowSize;\n' +
      'uniform float intensity;\nuniform float shading;\nuniform float aspect;\nuniform float gamma;\n' +
      'void main () {\n' +
      '  float d = texture2D(uTexture, vUv).x;\n' +
      '  float dl = texture2D(uTexture, vL).x;\n' +
      '  float dr = texture2D(uTexture, vR).x;\n' +
      '  float dt = texture2D(uTexture, vT).x;\n' +
      '  float db = texture2D(uTexture, vB).x;\n' +
      '  vec3 n = normalize(vec3(dl - dr, db - dt, 0.08));\n' +
      '  float lit = clamp(dot(n, normalize(vec3(-0.25, -0.55, 0.8))) , 0.0, 1.0);\n' +
      '  float a = 1.0 - exp(-pow(max(d, 0.0), gamma) * intensity);\n' +
      '  vec3 col = mix(colorLow, colorHigh, smoothstep(0.05, 0.85, vUv.y));\n' +
      '  vec2 g = vUv - glowPos;\n  g.x *= aspect;\n' +
      '  col = mix(col, glowColor, 0.55 * exp(-dot(g, g) / glowSize));\n' +
      '  col *= mix(1.0, 0.72 + 0.45 * lit, shading);\n' +
      '  a *= clamp(vUv.x / 0.16, 0.0, 1.0) * clamp((1.0 - vUv.x) / 0.16, 0.0, 1.0);\n' +
      '  a *= clamp(vUv.y / 0.08, 0.0, 1.0) * clamp((1.0 - vUv.y) / 0.26, 0.0, 1.0);\n' +
      '  gl_FragColor = vec4(col * a, a);\n' +
      '}\n'
  };

  /* ---------------------------------------------------------------- */
  /* WebGL helpers                                                    */
  /* ---------------------------------------------------------------- */
  function renderable(gl, internalFormat, format, type) {
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    var ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fbo);
    gl.deleteTexture(tex);
    return ok;
  }

  function pickFormat(gl, isGL2, type, want) {
    if (!isGL2) return renderable(gl, gl.RGBA, gl.RGBA, type) ? { i: gl.RGBA, f: gl.RGBA } : null;
    var chain = { r: [[gl.R16F, gl.RED], [gl.RG16F, gl.RG], [gl.RGBA16F, gl.RGBA]],
                  rg: [[gl.RG16F, gl.RG], [gl.RGBA16F, gl.RGBA]] }[want];
    for (var k = 0; k < chain.length; k++) {
      if (renderable(gl, chain[k][0], chain[k][1], type)) return { i: chain[k][0], f: chain[k][1] };
    }
    return null;
  }

  function getContext(canvas) {
    var attrs = { alpha: true, depth: false, stencil: false, antialias: false, premultipliedAlpha: true, preserveDrawingBuffer: false };
    var gl = canvas.getContext('webgl2', attrs);
    var isGL2 = !!gl;
    if (!gl) gl = canvas.getContext('webgl', attrs) || canvas.getContext('experimental-webgl', attrs);
    if (!gl) return null;
    var type, linear;
    if (isGL2) {
      gl.getExtension('EXT_color_buffer_float');
      gl.getExtension('EXT_color_buffer_half_float');
      type = gl.HALF_FLOAT;
      linear = true;
    } else {
      var hf = gl.getExtension('OES_texture_half_float');
      if (!hf) return null;
      type = hf.HALF_FLOAT_OES;
      linear = !!gl.getExtension('OES_texture_half_float_linear');
    }
    var r = pickFormat(gl, isGL2, type, 'r');
    var rg = pickFormat(gl, isGL2, type, 'rg');
    if (!r || !rg) return null;
    return { gl: gl, type: type, linear: linear, r: r, rg: rg };
  }

  /* ---------------------------------------------------------------- */
  /* Smoke                                                            */
  /* ---------------------------------------------------------------- */
  function NRSmoke(canvas, options) {
    var ctx = getContext(canvas);
    if (!ctx) throw new Error('nr-smoke: WebGL half-float not available');
    this.canvas = canvas;
    this.ctx = ctx;
    this.gl = ctx.gl;
    this.o = options || {};
    this.t = 0;
    this.running = false;
    this.slowFrames = 0;
    this.quality = 1;
    this.sources = [];
    this.pointer = null;
    this.tick = this.tick.bind(this);
    this.setup();
    this.resize();
  }

  NRSmoke.prototype.setup = function () {
    var gl = this.gl;
    var kw = this.ctx.linear ? '' : '#define MANUAL_FILTERING\n';
    var vs = this.shader(gl.VERTEX_SHADER, VERT);
    var self = this;
    this.p = {};
    Object.keys(FS).forEach(function (name) {
      self.p[name] = self.program(vs, kw + FRAG_HEAD + FS[name]);
    });

    this.quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW);
    this.index = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.index);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);
    gl.disable(gl.BLEND);
  };

  NRSmoke.prototype.shader = function (type, src) {
    var gl = this.gl;
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  };

  NRSmoke.prototype.program = function (vs, fsSrc) {
    var gl = this.gl;
    var pr = gl.createProgram();
    gl.attachShader(pr, vs);
    gl.attachShader(pr, this.shader(gl.FRAGMENT_SHADER, fsSrc));
    gl.bindAttribLocation(pr, 0, 'aPosition');
    gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(pr));
    var u = {};
    var n = gl.getProgramParameter(pr, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) {
      var name = gl.getActiveUniform(pr, i).name;
      u[name] = gl.getUniformLocation(pr, name);
    }
    return { id: pr, u: u };
  };

  NRSmoke.prototype.fbo = function (w, h, fmt) {
    var gl = this.gl;
    var filter = this.ctx.linear ? gl.LINEAR : gl.NEAREST;
    gl.activeTexture(gl.TEXTURE0);
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, fmt.i, w, h, 0, fmt.f, this.ctx.type, null);
    var fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    return { tex: tex, fb: fb, w: w, h: h, tx: 1 / w, ty: 1 / h };
  };

  NRSmoke.prototype.double = function (w, h, fmt) {
    var a = this.fbo(w, h, fmt);
    var b = this.fbo(w, h, fmt);
    return { read: a, write: b, w: w, h: h, tx: 1 / w, ty: 1 / h,
      swap: function () { var t = this.read; this.read = this.write; this.write = t; } };
  };

  NRSmoke.prototype.free = function () {
    var gl = this.gl;
    [this.velocity, this.dye, this.pressure].forEach(function (d) {
      if (!d) return;
      [d.read, d.write].forEach(function (f) { gl.deleteTexture(f.tex); gl.deleteFramebuffer(f.fb); });
    });
    [this.divergenceFbo, this.curlFbo].forEach(function (f) {
      if (f) { gl.deleteTexture(f.tex); gl.deleteFramebuffer(f.fb); }
    });
  };

  function res(base, w, h) {
    var ar = w / h;
    if (ar < 1) ar = 1 / ar;
    var min = Math.max(8, Math.round(base));
    var max = Math.round(base * ar);
    return w > h ? { w: max, h: min } : { w: min, h: max };
  }

  NRSmoke.prototype.resize = function () {
    var rect = this.canvas.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var w = Math.max(2, Math.round(rect.width * dpr));
    var h = Math.max(2, Math.round(rect.height * dpr));
    if (w === this.cw && h === this.ch && this.velocity) return;
    this.cw = this.canvas.width = w;
    this.ch = this.canvas.height = h;
    this.aspect = w / h;
    var small = (window.innerWidth || rect.width) < 750;
    this.half = small;
    var simBase = (small ? 96 : 128) * (this.quality < 1 ? 0.75 : 1);
    var dyeBase = Math.min(Math.min(w, h), (small ? 420 : 640) * this.quality);
    if (!this.ctx.linear) dyeBase = Math.min(w, h);
    var sim = res(simBase, w, h);
    var dye = res(dyeBase, w, h);
    this.free();
    this.velocity = this.double(sim.w, sim.h, this.ctx.rg);
    this.pressure = this.double(sim.w, sim.h, this.ctx.r);
    this.divergenceFbo = this.fbo(sim.w, sim.h, this.ctx.r);
    this.curlFbo = this.fbo(sim.w, sim.h, this.ctx.r);
    this.dye = this.double(dye.w, dye.h, this.ctx.r);
    this.simH = sim.h;
  };

  /* sources: [{x, y}] in canvas uv (0..1, y up) */
  NRSmoke.prototype.setSources = function (list) { this.sources = list || []; };

  NRSmoke.prototype.use = function (prog) { this.gl.useProgram(prog.id); return prog.u; };

  NRSmoke.prototype.bind = function (unit, target) {
    var gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, target.tex);
    return unit;
  };

  NRSmoke.prototype.blit = function (target) {
    var gl = this.gl;
    if (target) {
      gl.viewport(0, 0, target.w, target.h);
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fb);
    } else {
      gl.viewport(0, 0, this.cw, this.ch);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
  };

  NRSmoke.prototype.splat = function (target, x, y, value, radius, blend) {
    var gl = this.gl;
    var u = this.use(this.p.source);
    gl.uniform2f(u.texelSize, target.tx, target.ty);
    gl.uniform1i(u.uTarget, this.bind(0, target.read));
    gl.uniform1f(u.aspectRatio, this.aspect);
    gl.uniform2f(u.point, x, y);
    gl.uniform4f(u.value, value[0], value[1], value[2], value[3]);
    gl.uniform1f(u.radius, radius);
    gl.uniform1f(u.blend, blend);
    this.blit(target.write);
    target.swap();
  };

  NRSmoke.prototype.step = function (dt) {
    var gl = this.gl;
    var o = this.o;
    var amt = o.amount;
    var t = this.t;
    var v = this.velocity;
    var u;

    /* emit: smoke leaves each source with a lazy, uneven sway */
    for (var i = 0; i < this.sources.length; i++) {
      var s = this.sources[i];
      var ph = i * 1.7;
      var sway = Math.sin(t * 1.1 + ph) * 0.55 + Math.sin(t * 2.3 + ph * 2.1) * 0.3 + Math.sin(t * 5.7 + ph) * 0.15;
      var drift = Math.sin(t * 0.37 + ph) * 0.006;
      var puff = 0.78 + 0.22 * Math.sin(t * 0.9 + ph * 3.0) * Math.sin(t * 0.23 + ph);
      var r = o.radius * (s.w || 1);
      this.splat(v, s.x + drift, s.y, [sway * o.sway, o.lift * (0.9 + 0.1 * puff), 0, 0], r * 1.6, 1);
      this.splat(this.dye, s.x + drift, s.y, [amt * puff, 0, 0, 1], r, 1);
    }

    /* hand through the smoke */
    if (this.pointer && this.pointer.moved) {
      var pt = this.pointer;
      this.splat(v, pt.x, pt.y, [pt.dx * o.stir, pt.dy * o.stir, 0, 0], 0.0035, 0);
      pt.moved = false;
    }

    u = this.use(this.p.curl);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    this.blit(this.curlFbo);

    u = this.use(this.p.forces);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    gl.uniform1i(u.uCurl, this.bind(1, this.curlFbo));
    gl.uniform1i(u.uDensity, this.bind(2, this.dye.read));
    gl.uniform1f(u.curl, o.curl);
    gl.uniform1f(u.buoyancy, o.buoyancy);
    gl.uniform1f(u.dt, dt);
    gl.uniform1f(u.time, t);
    gl.uniform1f(u.wind, (Math.sin(t * 0.13) * 0.7 + Math.sin(t * 0.051 + 2.0) * 0.3) * o.wind);
    gl.uniform1f(u.turbulence, o.turbulence);
    gl.uniform1f(u.aspect, this.aspect);
    gl.uniform1f(u.ambientDamp, o.ambientDamp === undefined ? 1.2 : o.ambientDamp);
    gl.uniform1f(u.maxSpeed, o.maxSpeed || o.lift * 3);
    gl.uniform1f(u.confineFloor, o.confineFloor === undefined ? 0.25 : o.confineFloor);
    this.blit(v.write);
    v.swap();

    u = this.use(this.p.divergence);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    this.blit(this.divergenceFbo);

    u = this.use(this.p.scale);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uTexture, this.bind(0, this.pressure.read));
    gl.uniform1f(u.value, 0.8);
    this.blit(this.pressure.write);
    this.pressure.swap();

    u = this.use(this.p.pressure);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uDivergence, this.bind(0, this.divergenceFbo));
    var iters = (this.warming || this.quality < 1) ? 12 : 20;
    for (var k = 0; k < iters; k++) {
      gl.uniform1i(u.uPressure, this.bind(1, this.pressure.read));
      this.blit(this.pressure.write);
      this.pressure.swap();
    }

    u = this.use(this.p.gradient);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1f(u.gradScale, o.gradScale || 0.5);
    gl.uniform1i(u.uPressure, this.bind(0, this.pressure.read));
    gl.uniform1i(u.uVelocity, this.bind(1, v.read));
    this.blit(v.write);
    v.swap();

    u = this.use(this.p.advect);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform2f(u.uVelTexel, v.tx, v.ty);
    gl.uniform2f(u.uSrcTexel, v.tx, v.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    gl.uniform1i(u.uSource, this.bind(0, v.read));
    gl.uniform1f(u.dt, dt);
    gl.uniform1f(u.dissipation, o.velocityDissipation);
    this.blit(v.write);
    v.swap();

    gl.uniform2f(u.texelSize, this.dye.tx, this.dye.ty);
    gl.uniform2f(u.uSrcTexel, this.dye.tx, this.dye.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    gl.uniform1i(u.uSource, this.bind(1, this.dye.read));
    gl.uniform1f(u.dissipation, o.densityDissipation);
    this.blit(this.dye.write);
    this.dye.swap();
  };

  NRSmoke.prototype.render = function () {
    var gl = this.gl;
    var o = this.o;
    var u = this.use(this.p.display);
    gl.uniform2f(u.texelSize, this.dye.tx, this.dye.ty);
    gl.uniform1i(u.uTexture, this.bind(0, this.dye.read));
    gl.uniform3f(u.colorLow, o.colorLow[0], o.colorLow[1], o.colorLow[2]);
    gl.uniform3f(u.colorHigh, o.colorHigh[0], o.colorHigh[1], o.colorHigh[2]);
    gl.uniform3f(u.glowColor, o.glowColor[0], o.glowColor[1], o.glowColor[2]);
    gl.uniform2f(u.glowPos, this.glowPos ? this.glowPos[0] : 0.5, this.glowPos ? this.glowPos[1] : 0.5);
    gl.uniform1f(u.glowSize, o.glowSize);
    gl.uniform1f(u.aspect, this.aspect);
    gl.uniform1f(u.intensity, o.intensity);
    gl.uniform1f(u.shading, o.shading);
    gl.uniform1f(u.gamma, o.gamma || 1);
    this.blit(null);
  };

  NRSmoke.prototype.advance = function (steps, dt) {
    for (var i = 0; i < steps; i++) { this.t += dt; this.step(dt); }
  };

  /* Pre-run the simulation a few steps per frame so the plume is already
     standing when the section scrolls in, without one long GPU stall. */
  NRSmoke.prototype.warm = function (steps, perFrame, dt, done) {
    var self = this;
    var left = steps;
    self.warming = true;
    function run() {
      if (self.dead) return;
      var n = Math.min(perFrame, left);
      self.advance(n, dt);
      left -= n;
      if (left > 0) {
        self.warmRaf = requestAnimationFrame(run);
      } else {
        self.warming = false;
        self.render();
        if (done) done();
      }
    }
    run();
  };

  NRSmoke.prototype.tick = function (now) {
    if (!this.running) return;
    if (this.half && (this.frame = (this.frame || 0) + 1) % 2) {
      this.raf = requestAnimationFrame(this.tick);
      return;
    }
    var dt = this.last ? (now - this.last) / 1000 : 1 / 60;
    this.last = now;
    if (dt > 0.034) this.slowFrames++;
    else if (this.slowFrames > 0) this.slowFrames -= 0.25;
    if (this.slowFrames > 90 && this.quality === 1) {
      this.quality = 0.6;
      this.cw = 0;
      this.resize();
    }
    dt = Math.min(Math.max(dt, 1 / 120), 1 / 30) * (this.o.timeScale || 1);
    this.t += dt;
    this.step(dt);
    this.render();
    this.raf = requestAnimationFrame(this.tick);
  };

  NRSmoke.prototype.start = function () {
    if (this.running || this.warming || this.dead) return;
    this.running = true;
    this.last = 0;
    this.raf = requestAnimationFrame(this.tick);
  };

  NRSmoke.prototype.stop = function () {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  };

  NRSmoke.prototype.destroy = function () {
    this.stop();
    this.dead = true;
    if (this.warmRaf) cancelAnimationFrame(this.warmRaf);
    this.free();
    var lose = this.gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
  };

  window.NRSmoke = NRSmoke;

  /* ---------------------------------------------------------------- */
  /* Section                                                          */
  /* ---------------------------------------------------------------- */
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function hexToRgb(hex, fallback) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec((hex || '').trim());
    if (!m) return fallback;
    return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
  }

  function smokeOptions(section) {
    var level = parseFloat(section.getAttribute('data-smoke'));
    if (isNaN(level)) level = 75;
    var k = Math.max(0, Math.min(level, 100)) / 100;
    var speed = parseFloat(section.getAttribute('data-smoke-speed'));
    if (isNaN(speed)) speed = 50;
    return {
      /* slow motion: the whole simulation runs at this fraction of real time,
         so the smoke keeps the same shape and only moves slower */
      timeScale: Math.max(0.1, Math.min(speed, 100)) / 100,
      amount: 0.45 + 0.87 * k,
      intensity: 1.6 + 1.6 * k,
      gamma: 1.5,
      radius: 0.0005 + 0.0008 * k,
      lift: 40 + 20 * k,
      sway: 18,
      buoyancy: 18 + 10.7 * k,
      curl: 16,
      wind: 6,
      turbulence: 1.5,
      stir: 1400,
      velocityDissipation: 0.3,
      densityDissipation: 0.85 - 0.4 * k,
      shading: 0.85,
      colorLow: [0.97, 0.91, 0.82],
      colorHigh: [0.95, 0.95, 0.93],
      glowColor: [1.0, 0.78, 0.45],
      glowSize: 0.05
    };
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function openDrawer(attempt) {
    attempt = attempt || 0;
    var drawer = document.querySelector('m-cart-drawer, cart-drawer, [data-cart-drawer]');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (attempt < 10) {
      setTimeout(function () { openDrawer(attempt + 1); }, 150);
      return;
    }
    window.location.href = root() + 'cart';
  }

  function initCart(section) {
    var form = section.querySelector('[data-nr-spot-form]');
    var input = section.querySelector('[data-nr-spot-input]');
    var button = section.querySelector('[data-nr-spot-add]');
    var label = section.querySelector('[data-nr-spot-label]');
    var status = section.querySelector('[data-nr-spot-status]');

    function clamp(value) {
      var max = input ? parseInt(input.getAttribute('max'), 10) || 99 : 99;
      var n = parseInt(value, 10);
      if (isNaN(n) || n < 1) n = 1;
      return Math.min(n, max);
    }

    if (input) {
      section.addEventListener('click', function (e) {
        var step = e.target.closest && e.target.closest('[data-nr-spot-step]');
        if (!step || !section.contains(step) || step.disabled) return;
        input.value = clamp((parseInt(input.value, 10) || 1) + parseInt(step.getAttribute('data-nr-spot-step'), 10));
      });
      input.addEventListener('change', function () { input.value = clamp(input.value); });
    }

    if (!form || !button || !label || !status) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

      var idField = form.querySelector('[name="id"]');
      var qty = clamp(input ? input.value : 1);
      var original = label.textContent;

      button.setAttribute('aria-busy', 'true');
      button.classList.remove('is-added');
      status.textContent = '';
      status.classList.remove('is-error');

      var sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
      );

      var config = typeof window.fetchConfig === 'function'
        ? window.fetchConfig('javascript')
        : { headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
      config.method = 'POST';
      config.body = JSON.stringify({
        items: [{ id: idField.value, quantity: qty }],
        sections: sections,
        sections_url: window.location.pathname
      });

      fetch(root() + 'cart/add.js', config)
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) throw new Error(res.description || res.message || '');
          var theme = window.MinimogTheme || {};
          if (window.MinimogEvents && theme.pubSubEvents) {
            window.MinimogEvents.emit(theme.pubSubEvents.cartUpdate, { cart: res });
          }
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: res } }));
          var added = section.getAttribute('data-added');
          button.classList.add('is-added');
          label.textContent = added;
          status.textContent = added;
          openDrawer();
          setTimeout(function () {
            button.classList.remove('is-added');
            label.textContent = original;
          }, 2400);
        })
        .catch(function (err) {
          status.textContent = (err && err.message) || section.getAttribute('data-error');
          status.classList.add('is-error');
        })
        .finally(function () {
          button.removeAttribute('aria-busy');
        });
    });
  }

  function initSmoke(section, onReady) {
    var canvas = section.querySelector('[data-nr-smoke]');
    var source = section.querySelector('[data-nr-smoke-source]');
    var level = parseFloat(section.getAttribute('data-smoke'));
    if (!canvas || !source || level === 0) return null;

    var smoke;
    try {
      smoke = new NRSmoke(canvas, smokeOptions(section));
    } catch (err) {
      section.classList.add('nr-spot--no-webgl');
      return null;
    }
    section.classList.add('nr-spot--webgl');
    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      smoke.stop();
      smoke.dead = true;
      section.classList.remove('nr-spot--webgl');
      section.classList.add('nr-spot--no-webgl');
    });

    /* Layout offsets (not getBoundingClientRect) so the reveal animation
       on the product doesn't shift where the smoke starts. */
    var wrap = canvas.parentNode;
    function place() {
      smoke.resize();
      var W = wrap.offsetWidth;
      var H = wrap.offsetHeight;
      if (!W || !H) return;
      var cx = source.offsetLeft + source.offsetWidth / 2 - wrap.offsetLeft;
      var top = source.offsetTop - wrap.offsetTop;
      var x = cx / W;
      var y = 1 - (top + source.offsetHeight * 0.16) / H;
      var spread = (source.offsetWidth * 0.05) / W;
      smoke.setSources([
        { x: x - spread, y: y, w: 1 },
        { x: x + spread * 0.8, y: y - 0.01, w: 0.8 }
      ]);
      smoke.glowPos = [x, 1 - (top + source.offsetHeight * 0.05) / H];
    }
    place();
    smoke.place = place;

    var lastX = null;
    var lastY = null;
    function move(clientX, clientY) {
      var c = canvas.getBoundingClientRect();
      var x = (clientX - c.left) / c.width;
      var y = 1 - (clientY - c.top) / c.height;
      if (x < 0 || x > 1 || y < 0 || y > 1) { lastX = null; return; }
      if (lastX !== null) smoke.pointer = { x: x, y: y, dx: (x - lastX), dy: (y - lastY), moved: true };
      lastX = x;
      lastY = y;
    }
    section.addEventListener('pointermove', function (e) { move(e.clientX, e.clientY); }, { passive: true });
    section.addEventListener('pointerleave', function () { lastX = null; }, { passive: true });

    var warm = parseInt(section.getAttribute('data-smoke-warmup'), 10);
    smoke.still = reduceMotion || section.hasAttribute('data-smoke-still');
    smoke.warm(isNaN(warm) ? 200 : warm, smoke.still ? 20 : 8, 1 / 30, function () {
      section.setAttribute('data-smoke-ready', '');
      if (onReady) onReady();
    });
    return smoke;
  }

  function init(section) {
    if (section.dataset.nrSpotReady === 'true') return;
    section.dataset.nrSpotReady = 'true';
    section.classList.add('nr-spot--js');

    initCart(section);

    var smoke = null;
    var visible = false;

    function sync() {
      if (!smoke) return;
      if (visible && !document.hidden && !smoke.still) smoke.start();
      else smoke.stop();
    }

    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1); };
    function ensureSmoke() {
      if (smoke || section.dataset.nrSmokeTried) return;
      section.dataset.nrSmokeTried = 'true';
      idle(function () {
        smoke = initSmoke(section, sync);
        section.nrSmoke = smoke;
        sync();
      }, { timeout: 400 });
    }

    if ('IntersectionObserver' in window) {
      /* start warming the smoke well before the section scrolls in */
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) ensureSmoke();
        });
      }, { rootMargin: '1500px 0px' }).observe(section);
      /* run the animation only while any part is on screen; reveal once */
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible = entry.isIntersecting;
          if (visible) ensureSmoke();
          if (entry.intersectionRatio >= 0.1) section.classList.add('is-revealed');
          sync();
        });
      }, { threshold: [0, 0.1, 0.25] }).observe(section);
    } else {
      section.classList.add('is-revealed');
      visible = true;
      ensureSmoke();
      sync();
    }

    document.addEventListener('visibilitychange', sync);
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (smoke && smoke.place) { smoke.place(); if (smoke.still) smoke.render(); }
      }, 150);
    });

    section.nrDestroy = function () {
      if (smoke) smoke.destroy();
      smoke = null;
    };
  }

  window.NRSpotlight = {
    boot: function (scope) {
      (scope || document).querySelectorAll('[data-nr-spot]').forEach(init);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.NRSpotlight.boot(document); });
  } else {
    window.NRSpotlight.boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { window.NRSpotlight.boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target && e.target.querySelector && e.target.querySelector('[data-nr-spot]');
    if (el && el.nrDestroy) el.nrDestroy();
  });
})();
